import { useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GCDSDropdown, GCDSSwitcher } from '@gocanvas/gcds/components'

import {
  AddIcon,
  EditIcon,
  CalculatorIcon,
  SubtractIcon,
  MultiplyIcon,
  DivideIcon,
  DeleteIcon,
} from '@gocanvas/gcds/icons'

import {
  entriesSelector,
  operationsSelector,
  activeEntrySelector,
  eligibleDependentEntriesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'

import { operationTypesSelector } from 'apps/formsCreationKit/store/selectors/types'

import { guidNumber } from 'apps/studio/lib/utils/entities'
import { whiteListedTypesForCalculation } from 'apps/studio/lib/utils/entryTypes'
import { operationsActions } from 'apps/formsCreationKit/store/reducers/entities/operations'

const {
  addOperation,
  deleteEmptyOperations,
  deleteOperation,
  updateOperation,
} = operationsActions

const createOperation = ({
  guid,
  id,
  operation_type_id = 1,
  operation_type = 'add',
  ...operationData
}) => {
  return addOperation({
    ...operationData,
    entry: guid,
    entry_id: id,
    operation_type_id,
    operation_type,
    id: guidNumber(),
  })
}

// findOptionsAndSelectedField :: (Array, String) -> Object{Array, Object}
// given a list of sheets and entries, find valid options for the select input
const findOptionsAndSelectedField = (
  calculationSheetsAndEntries,
  parentEntryGuid
) => {
  let selectedField
  const options = calculationSheetsAndEntries.reduce(
    (screens, { entries = [], screenLabel }) => {
      const validEntries = entries.reduce((acc, entry) => {
        const { type, label, placeholder, guid, sheet_id } = entry
        if (whiteListedTypesForCalculation.includes(type)) {
          const option = {
            label: label ? label : placeholder,
            value: guid,
            sheet_id,
          }
          if (guid === parentEntryGuid) {
            selectedField = option
          }
          acc.push(option)
        }
        return acc
      }, [])

      if (validEntries.length) {
        screens.push({
          label: screenLabel,
          options: validEntries,
        })
      }
      return screens
    },
    []
  )

  return {
    options,
    selectedField,
  }
}

// findEntryInSheetOptions :: (Array, String) -> Object
const findEntryInSheetOptions = (sheetOptions, targetGuid) => {
  let foundEntry = null
  for (const { options } of sheetOptions) {
    foundEntry = options.find(({ value: guid }) => guid === targetGuid)
    if (foundEntry) {
      break
    }
  }
  return foundEntry
}

const CalculationValue = ({
  parentEntryGuid,
  value,
  id,
  entry,
  sheet_id,
  index,
}) => {
  const dispatch = useDispatch()

  const calculationSheetsAndEntries = useSelector(
    eligibleDependentEntriesSelector
  )

  const { options, selectedField } = findOptionsAndSelectedField(
    calculationSheetsAndEntries,
    parentEntryGuid
  )

  const isConstantValue = !parentEntryGuid && value !== '' && !isNaN(value)
  if (isConstantValue) {
    options.unshift({
      label: 'Constant',
      options: [{ label: value, value }],
    })
  }

  const calculationValue = isConstantValue
    ? { label: value, value }
    : selectedField || ''

  const onChange = selectedOption => {
    if (selectedOption) {
      const { value: updatedValue } = selectedOption
      const payload = {
        id,
        index,
        guid: entry,
        sheet_id: String(sheet_id),
      }

      // get the full entry from valid sheet/entry options
      const foundEntry = findEntryInSheetOptions(options, updatedValue)

      if (foundEntry) {
        // Value is an entry
        payload.parentEntryGuid = updatedValue
        payload.parentEntrySheetId = String(foundEntry.sheet_id)
        payload.value = ''
      } else {
        // Value is a constant
        payload.parentEntryGuid = null
        payload.value = updatedValue
      }

      dispatch(id ? updateOperation(payload) : createOperation(payload))
    } else {
      // Value was cleared, update store with empty operation
      dispatch(
        updateOperation({
          id,
          parentEntryGuid: null,
          value: '',
        })
      )
    }
  }

  const sanitizeInput = inputValue => {
    // Only allow numeric characters and a decimal point
    let sanitizedInput = inputValue.replace(/[^\d.]/g, '')
    // Only allow one decimal point
    const periodCount = (sanitizedInput.match(/\./g) || []).length
    if (periodCount > 1) {
      sanitizedInput = sanitizedInput.replace(/\./g, (_, offset, string) => {
        return string.indexOf('.') === offset ? '.' : ''
      })
    }
    return sanitizedInput
  }

  return (
    <div className="calculation-value--dropdown">
      <GCDSDropdown
        value={calculationValue}
        options={options}
        onChange={onChange}
        portal={true}
        inputType="secondary"
        size="large"
        additionalProps={{
          isClearable: true,
          createable: true,
          createOptionPosition: 'first',
          formatCreateLabel: inputValue => `Add Constant: ${inputValue}`,
          onInputChange: inputValue => sanitizeInput(inputValue),
          placeholder: 'Select or type a value...',
        }}
        components={{ Placeholder: () => <div className="calculation--placeholder">Select or type a value...</div> }}
      />
    </div>
  )
}

const operationIcons = {
  add: AddIcon,
  subtract: SubtractIcon,
  multiply: MultiplyIcon,
  divide: DivideIcon,
}

const OperationSwitcher = ({ operation_type_id = 1, id, entry, index }) => {
  const dispatch = useDispatch()
  const operationOptions = useSelector(operationTypesSelector)
  const options = useMemo(
    () =>
      operationOptions.map(({ operation_type, id }) => ({
        IconComponent: operationIcons[operation_type],
        value: id,
      })),
    [operationOptions]
  )

  const onChange = updatedValue => {
    const { operation_type } = operationOptions.find(
      ({ id: operationOptionId }) => operationOptionId == updatedValue
    )
    const update = id ? updateOperation : createOperation

    dispatch(
      update({
        id,
        operation_type,
        index,
        guid: entry,
        operation_type_id: updatedValue,
      })
    )
  }

  return (
    <GCDSSwitcher
      name={`operation-switcher-${index}`}
      options={options}
      showLabels={false}
      value={operation_type_id}
      onChange={onChange}
    />
  )
}

const ensureMinimumCalculationOptions = (calculations, defaultOption) => {
  const minimumOptions = 2
  const calculationCount = calculations.length

  if (calculationCount > minimumOptions) {
    return calculations.sort((a, b) => a.index - b.index)
  }

  const neededOptions = minimumOptions - calculationCount
  const newOptions = Array(neededOptions).fill(defaultOption)
  calculations.forEach(calc => {
    newOptions.splice(calc.index, 0, calc)
  })
  return newOptions
}

const CalculationItems = ({ toggle }) => {
  const dispatch = useDispatch()
  const allOperations = useSelector(operationsSelector)
  const activeEntry = useSelector(activeEntrySelector)
  const { guid, sheet_id } = activeEntry || {}
  const entryCalculations = allOperations.filter(({ entry }) => entry === guid)
  const operationPlaceholder = {
    entry: guid,
  }
  const calculations = ensureMinimumCalculationOptions(
    entryCalculations,
    operationPlaceholder
  )
  const hasPlaceholderOperations = calculations.some(({ id }) => !id)

  return (
    <>
      <div className="calculation--container">
        {calculations.map((calculation = {}, i) => {
          const { id } = calculation
          const calculationKey = id ? `calculation-${id}` : `placeholder-${i}`
          return (
            <div
              className="calculation-item--container"
              key={calculationKey}
            >
              {i > 0 && <OperationSwitcher {...calculation} index={i} />}
              <div className="calculation--item">
                <CalculationValue
                  {...calculation}
                  sheet_id={sheet_id}
                  index={i}
                />
                <div className="item--control">
                  {calculations.length > 2 && (
                    <button
                      className="gcds--button button--icon is-danger"
                      type="button"
                      onClick={() => dispatch(deleteOperation(calculation.id))}
                    >
                      <DeleteIcon />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
      <div className="calculation--controls">
        <button
          className={`gcds--button button--secondary${
            hasPlaceholderOperations ? ' is-disabled' : ''
          }`}
          type="button"
          disabled={hasPlaceholderOperations}
          onClick={() =>
            dispatch(createOperation({ guid, index: calculations.length + 1 }))
          }
        >
          <div className="button--label">Add Value</div>
        </button>
        <button
          className="gcds--button button--primary"
          type="button"
          onClick={() => {
            dispatch(deleteEmptyOperations({ entryGuid: guid }))
            toggle()
          }}
        >
          <div className="button--label">Done</div>
        </button>
      </div>
    </>
  )
}

const ShowCalculation = ({ guid, toggle }) => {
  const entryCalculations = useSelector(operationsSelector)
    .filter(({ entry }) => entry === guid)
  const entries = useSelector(entriesSelector)
  const hasCalculations = entryCalculations.length > 0

  const calculationTypeMap = {
    add: '+',
    subtract: '-',
    multiply: '*',
    divide: '/',
  }

  const calculationString = entryCalculations.reduce(
    (calcString, { operation_type, value, parentEntryGuid }, index) => {
      const isConstant = !parentEntryGuid
      const valueToDisplay = isConstant
        ? value
        : entries.find(({ guid }) => guid === parentEntryGuid).label

      if (isConstant && (!valueToDisplay || isNaN(valueToDisplay))) {
        return calcString
      } else if (index === 0) {
        return valueToDisplay
      } else {
        const operator = calculationTypeMap[operation_type]
        return `${calcString} ${operator} ${valueToDisplay}`
      }
    },
    ''
  )

  if (!hasCalculations) {
    return <EmptyControls toggle={toggle} />
  }

  return (
    <>
      <div className="calculation--string">{calculationString}</div>
      <div className="calculation--controls">
        <button
          className="gcds--button button--secondary"
          type="button"
          onClick={toggle}
        >
          <EditIcon />
          <span className="button--label">Edit</span>
        </button>
      </div>
    </>
  )
}

const EmptyControls = ({ toggle }) => (
  <button
    className="gcds--button button--secondary add-calculation--button"
    type="button"
    onClick={toggle}
  >
    <AddIcon className="icon--leading" />
    <span className="button--label">Add Calculation</span>
  </button>
)

const Calculation = ({ guid }) => {
  const [calculationState, setCalculationState] = useState('viewing')

  const CalculationComponents = {
    editing: () => (
      <CalculationItems toggle={() => setCalculationState('viewing')} />
    ),
    viewing: () => (
      <ShowCalculation
        guid={guid}
        toggle={() => setCalculationState('editing')}
      />
    ),
  }

  const CalculationComponent = CalculationComponents[calculationState]

  return (
    <div className="gcds--card calculation--field">
      <CalculationComponent />
    </div>
  )
}

export const CALCULATION_PANEL = 'Calculation'

export const CalculationPanel = {
  label: CALCULATION_PANEL,
  icon: CalculatorIcon,
  component: ({ entry }) => <Calculation {...entry} />,
  settings: [],
}
